import { Button } from "PFComponents/button";
import useForceUpdate from "PFCore/helpers/use_force_update";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./overwrites.less";
import CustomField from "./overwrites/custom_fields/custom_fields";
import MainCompany from "./overwrites/main_company";
import MainJobTitle from "./overwrites/main_job_title";
import Names from "./overwrites/names";
import Position from "./overwrites/position";
import Summary from "./overwrites/summary";

const Overwrites = ({ exportableProfile, config }) => {
  const { hidden, isBlindModeOn, order } = config;
  const forceUpdate = useForceUpdate();
  const { t } = useTranslation();

  const [cfModified, setCfModified] = useState(0);
  const [positionModified, setPositionModified] = useState(0);

  const toggleAllPositions = () => {
    const allEnabled = exportableProfile.positions.every(({ enabled }) => enabled);
    exportableProfile.positions.forEach((position) => {
      exportableProfile.updatePosition(position.id, { enabled: !allEnabled });
    });
    forceUpdate();
  };

  const resetAllCustomFields = () => {
    exportableProfile.customFields.forEach((cf) => exportableProfile.resetCustomFieldValue(cf.type.name));
    setCfModified(0);
    forceUpdate();
  };

  const resetAllPositions = () => {
    exportableProfile.positions.forEach((position) => exportableProfile.resetPosition(position.id));
    setPositionModified(0);
    forceUpdate();
  };

  const customFieldsChanged = exportableProfile?.customFields
    ?.map(({ type }) => exportableProfile.hasChangedCustomFieldValue(type.name))
    .filter((item) => item);

  const positionsChanged = exportableProfile?.positions
    ?.map((pos) => exportableProfile.hasChangedPosition(pos.id))
    .filter((item) => item);

  const hasPositionsAvailable = order.includes("experience");

  return (
    <div className={css.root}>
      <Names exportableProfile={exportableProfile} isBlindModeOn={isBlindModeOn} />
      <MainJobTitle exportableProfile={exportableProfile} />
      <MainCompany exportableProfile={exportableProfile} />
      {!hidden.summary && <Summary exportableProfile={exportableProfile} />}

      <Section title={t("customFieldsTitle")}>
        <div className={css.cfActionsRow}>
          {customFieldsChanged.length > 1 && (
            <Button small onClick={resetAllCustomFields}>
              {t("resetAll")}
            </Button>
          )}
        </div>
        <table className={css.table}>
          <tbody>
            {order.map((name) => {
              if (name === "summary" || name === "experience") {
                return false;
              }
              if (hidden[name]) {
                return false;
              }

              const customType = exportableProfile.customTypes.find((type) => type.name === name);
              if (!customType) {
                return;
              }

              return (
                <CustomField
                  exportableProfile={exportableProfile}
                  key={name}
                  name={name}
                  displayAs={customType.display_as}
                  onModified={setCfModified}
                  timesModified={cfModified}
                />
              );
            })}
          </tbody>
        </table>
      </Section>

      {!hidden.experience && hasPositionsAvailable && (
        <Section title={t("positions")}>
          <div className={css.actionsRow}>
            <Button small onClick={toggleAllPositions}>
              {t("toggleAll")}
            </Button>

            {positionsChanged.length > 1 && (
              <Button small onClick={resetAllPositions}>
                {t("resetAll")}
              </Button>
            )}
          </div>

          <table className={css.table}>
            <tbody>
              {exportableProfile.positions.map((position, index) => (
                <Position
                  key={position.id}
                  exportableProfile={exportableProfile}
                  index={index}
                  position={position}
                  onModified={setPositionModified}
                  timesModified={positionModified}
                />
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
};

Overwrites.propTypes = {
  exportableProfile: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired
};

const Section = ({ children, title }) => {
  const [open, setOpen] = useState(false);

  if (React.Children.toArray(children).filter(Boolean).length === 0) {
    return false;
  }

  return (
    <>
      <Button kind="blank" onClick={() => setOpen(!open)} className={css.sectionButton}>
        <h3 className={css.sectionName}>
          <span style={open ? { transform: "rotate(90deg)" } : null} />
          {title}
        </h3>
      </Button>
      {open && <>{children}</>}
    </>
  );
};

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node
};

export default Overwrites;
